import {getEnv} from '@config/getEnv';
import {NextHeaders} from '@config/headers';
import {AppLocale} from '@config/locales';
import {
    CancelException,
    FetchException,
    FetchNetworkException,
    MaintenanceException,
    SessionException,
} from '@core/exceptions';
import {getIsServer} from '@utils/getIsServer';
import {getLocale} from '@utils/getLocale';
import {redirectToNotFound} from '@utils/redirectToNotFound';
import queryString from 'query-string';
import {IFetchParams, IFetchResponse, MaintenanceMode} from './http.model';

export const addMethodsToFetch = (
    fetchMethod: <T>(
        url: string,
        params: IFetchParams,
    ) => Promise<IFetchResponse<T>>,
) => {
    return {
        get: <T = unknown>(url: string, options?: IFetchParams) =>
            fetchMethod<T>(
                url +
                    (options?.params
                        ? '?' +
                          queryString.stringify(options?.params, {
                              arrayFormat: 'bracket',
                          })
                        : ''),
                {
                    method: 'GET',
                    headers: {Accept: 'application/json'},
                    ...options,
                },
            ),
        delete: <T = unknown>(url: string, options?: IFetchParams) =>
            fetchMethod<T>(
                url +
                    (options?.params
                        ? '?' +
                          queryString.stringify(options?.params, {
                              arrayFormat: 'bracket',
                          })
                        : ''),
                {
                    method: 'DELETE',
                    headers: {Accept: 'application/json'},
                    ...options,
                },
            ),
        post: <T = unknown, K = unknown>(
            url: string,
            body?: K,
            options?: IFetchParams,
        ) =>
            fetchMethod<T>(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body:
                    body instanceof FormData || body instanceof URLSearchParams
                        ? body
                        : JSON.stringify(body),
                ...options,
            }),
        patch: <T = unknown>(
            url: string,
            body?: unknown,
            options?: IFetchParams,
        ) =>
            fetchMethod<T>(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
                ...options,
            }),
        put: <T = unknown>(
            url: string,
            body?: unknown,
            options?: IFetchParams,
        ) =>
            fetchMethod<T>(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
                ...options,
            }),

        head: <T = unknown>(url: string, options?: IFetchParams) =>
            fetchMethod<T>(url, {
                method: 'HEAD',
                ...options,
            }),
    };
};

export const makeRequest = async <T>(
    url: string,
    params: IFetchParams,
): Promise<IFetchResponse<T>> => {
    if (!getEnv('NEXT_PUBLIC_URL_BASE')) {
        // next-runtime-env has an issue with losing the client side env on hard refresh on page errors, this is a workaround to stop an infinite loop of redirects against "https://host/undefined"
        console.error('Missing ENV, aborting request', {url});
        throw new Error('Missing ENV, aborting request');
    }
    if (url.includes(getEnv('NEXT_PUBLIC_URL_BASE'))) {
        // passing locale header back to nextjs api to ensure correct locale is
        // used during SSR api route calls
        params.headers = {
            ...params.headers,
            [NextHeaders.LOCALE]: await getLocale(),
        };
    }
    const res = await fetch(url, params).catch(error => {
        checkThrowCancelException(error);
        throw new FetchNetworkException('Request failed: ' + error.message);
    });

    let data: T;
    try {
        data = await res.json();
    } catch (error) {
        data = null as T;
    }

    const response: IFetchResponse = {
        headers: res.headers,
        status: res.status,
        data: data,
    };

    if (getIsServer() && params.redirectNotFoundStatus?.includes(res.status)) {
        await redirectToNotFound();
    }

    if (!(res.status >= 200 && res.status < 300)) {
        throw new FetchException(
            response.data?.message || 'API Error',
            response,
        );
    }

    return response as IFetchResponse<T>;
};

export const checkThrowCancelException = (error: FetchException) => {
    if (error.name === 'AbortError' || error instanceof CancelException) {
        throw new CancelException();
    }
};

export const checkThrowMaintenanceException = (error: FetchException) => {
    const {maintenanceMode} = getMaintenanceMode(error.response);
    if (maintenanceMode) {
        console.warn(error);
        throw new MaintenanceException();
    }
};

const assumeMaintenanceStatusCodes = [500, 501, 502, 503, 504];

export const getMaintenanceMode = (response?: IFetchResponse) => {
    let maintenanceMode = response?.headers.get('x-maintenance-mode') as
        | MaintenanceMode
        | undefined;
    const maintenanceUntil = response?.headers.get('x-maintenance-until') as
        | string
        | undefined;
    const maintenanceFrom = response?.headers.get('x-maintenance-from') as
        | string
        | undefined;

    if (
        !maintenanceMode &&
        response &&
        assumeMaintenanceStatusCodes.includes(response.status)
    ) {
        maintenanceMode = MaintenanceMode.FULL;
    }

    return {maintenanceMode, maintenanceFrom, maintenanceUntil};
};

export const checkIsSessionError = (error: FetchException) => {
    const badAuthCodes = [401, 403];
    return (
        badAuthCodes.includes(error?.response?.status as number) ||
        error instanceof SessionException
    );
};

export const finalizeUrl = (url: string, locale: AppLocale) => {
    let finalUrl = url.replace('$locale', locale);

    const localeSlugPattern = new RegExp(/\$localeSlug\(([^)]+)\)/);

    if (localeSlugPattern.test(url)) {
        const matchRegex = finalUrl.match(localeSlugPattern);

        const localeSlug = matchRegex?.[1]
            ?.split(';')
            .find(slug => slug.includes(`${locale}:`))
            ?.split(':')?.[1];

        finalUrl = finalUrl.replace(localeSlugPattern, localeSlug!);
    }

    // fetching urls on node requires a full url including domain, on web that works because it automatically takes the base url
    if (!finalUrl.startsWith('http')) {
        // atm we just assume, that we only use nextjs api urls so that should work fine, maybe we can optimise and use the local api, so we don't involve dns and so on!
        finalUrl = getEnv('NEXT_PUBLIC_URL_BASE') + url;
    }

    return finalUrl;
};
